import { useMutation } from '@tanstack/react-query'
import { useState, useContext, useEffect } from 'react'
import type { ChangeEvent } from 'react'
import styled from 'styled-components'

import { MODALS_ID } from '../../../constants'

import { toast } from '../../../utils'

import { StoresContext } from '../../../provider/StoreProvider'
import { ModalContext } from '../../../provider/ModalProvider'

import { useNavigate } from '../../../hooks/useNavigate'
import { useUserInfo } from '../../../hooks/useUserInfo'

import { updateUserInfo } from '../../../api/my'

import { Head } from '../../../components/head/Head'
import { Wrap } from '../../../components/container/Wrap'
import { Content } from '../../../components/container/Content'
import { CenterWrap, ColumnItem } from '../../../components/common'
import { Button } from '../../../components/button'
import { Input } from '../../../components/input'
import { Text } from '../../../components/text'
import { Footer, FOOTER_MIN_HEIGHT } from '../../../components/footer'
import { Header } from '../../../components/header'
import { Spinner } from '../../../components/spinner/Spinner'
import SuccessEmailModal from '../modal/SuccessEmailModal'
import { NoticeContainer } from '../../../components/noticeContainer/NoticeContainer'

const HEAD_INFO = {
  title: '이메일| 미술품 조각투자 플랫폼, 아티피오',
  ogTitle: '이메일 | 미술품 조각투자 플랫폼, 아티피오',
  ogUrl: window.location.href,
}

export function Email() {
  const {
    commonStore: { setInvalidAccess },
  } = useContext(StoresContext)
  const { openModal, closeModal } = useContext(ModalContext)

  const [email, setEmail] = useState<string>('')
  const [isValid, setIsValid] = useState<boolean>(true)
  const [isChanged, setIsChanged] = useState<boolean>(false)

  const { data: userInfo, isPending, isError, isLogin, isCheckingLogin, refetch } = useUserInfo()
  const navigate = useNavigate()

  const { mutate } = useMutation({
    mutationFn: updateUserInfo,
    onSuccess: async () => {
      await refetch()

      openModal({
        id: MODALS_ID.SUCCESS_ADDRESS,
        Component: () => (
          <SuccessEmailModal
            onDismiss={() => {
              closeModal(MODALS_ID.SUCCESS_ADDRESS)
              navigate(-1)
            }}
          />
        ),
      })
    },
    onError: (error) => {
      toast('정보 수정에 실패했습니다. 잠시 후 다시 시도해주세요.', error)
    },
  })

  useEffect(() => {
    if (!isLogin && !isCheckingLogin) setInvalidAccess(true)
  }, [isLogin, isCheckingLogin])

  useEffect(() => {
    if (!userInfo?.email) return
    setEmail(userInfo.email)
  }, [userInfo])

  if (isPending || isError || !userInfo) {
    return (
      <>
        <Head {...HEAD_INFO} />
        <CenterWrap>
          <Spinner />
        </CenterWrap>
      </>
    )
  }

  const handleEmail = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    const valid = value.length ? regex.test(value) : true
    setIsValid(valid)
    setIsChanged(valid && userInfo.email !== value)
    setEmail(value)
  }

  const registerEmail = () => mutate({ email })

  return (
    <>
      <Head {...HEAD_INFO} />
      <Wrap>
        <Header type="SUB" title="이메일" />
        <StyledContent>
          <Container $gap={34}>
            <ColumnItem $gap={6}>
              <Text size={24} weight={600} lineHeight={34} block={true}>
                이메일 주소 입력 후
                <br />
                {userInfo.email ? '변경' : '저장'}하기 버튼을 눌러주세요.
              </Text>
            </ColumnItem>
            <ColumnItem $gap={14}>
              <Input value={email} placeholder="artipio@gmail.com" onChange={handleEmail} />
              {!isValid && (
                <Text size={12} color="error">
                  올바른 이메일 형식이 아니에요.
                </Text>
              )}
            </ColumnItem>
          </Container>
          <NoticeContainer
            title="안내사항"
            list={[
              '등록하신 이메일을 통해 아티피오의 공지사항 및 다양한 소식을 전해 드려요.',
              '혹시 메일을 받지 못하셨다면, 스팸 메일함을 확인해 주세요.',
              '이메일을 잘못 입력하여 받으시는 불이익은 아티피오가 책임지지 않아요. 이 점 유의해 주세요.',
            ]}
            $backgroundColor="gray_1"
          />
        </StyledContent>
        <Footer>
          <Button
            type="primary"
            size="large"
            block={true}
            disabled={!email.trim() || !isValid || !isChanged}
            onClick={registerEmail}
          >
            {userInfo.email ? '변경하기' : '저장하기'}
          </Button>
        </Footer>
      </Wrap>
    </>
  )
}

const StyledContent = styled(Content)`
  > *:first-child {
    flex-shrink: 1;
  }
`

const Container = styled(ColumnItem)`
  height: 100%;
  padding: 56px 24px 24px;
`
